import React, { Fragment, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Text, ColorProvider, HeatStreakText, Spacing, Icon } from '../components';
import CircleText from '../components/chrome/CircleText';
import { css } from '@emotion/core';

const RankingWrapper = styled.div`
  background: ${({ theme }) => theme.colors.headerBg90};
  color: ${({ theme }) => theme.colors.white};
  margin-top: ${({ theme }) => theme.spacings.mega};
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StatRow = styled.div`
  flex-grow: 1;
  padding-top: 4px;
`;

const StatTopRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  > div:nth-of-type(1) {
    width: 37%;
  }

  > div:nth-of-type(2) {
    width: 21%;
  }

  > div:nth-of-type(3) {
    width: 21%;
  }

  > div:nth-of-type(4) {
    width: 21%;
  }
`;

const cellStyles = ({ textAlign = 'center' }) =>
  css`
    padding: 0 0.3em 0 0.3em;
    text-align: ${textAlign};
  `;

const Cell = styled('div')(cellStyles);

const RankingChangeRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const IconContainer = styled.div`
  margin-left: 2px;
  svg {
    vertical-align: baseline;
  }
`;

const ScreenReaderText = styled.span`
  display: none;
`;

const PersonalRanking = ({ data, selectedBoard }) => {
  const { strings, leaderboard, config } = useSelector((state) => state);
  const { language } = config;
  const { userStats, selectedChallenge } = leaderboard;
  const { correctPicks, currentChallenge, pickAccuracy, rank, rankChange } = data;
  const showRankingChange = selectedChallenge === currentChallenge || selectedBoard === 'season';
  const challenge = selectedChallenge || currentChallenge;
  const challengeData = userStats.challenges?.[challenge] || {};
  const rankChangeText = strings['leaderboard/personal-ranking/rank-change'];
  const { username, scoringStreak } = userStats;

  // eslint-disable-next-line no-unused-vars
  const [forceUpdate, setForceUpdate] = useState(false);

  useEffect(() => {
    console.log('rerendered!');
    setForceUpdate((prev) => !prev);
  }, [selectedBoard]);

  const caretTooltip = !challengeData.rankChange
    ? strings['leaderboard/personal-ranking/caret-tooltip/none']
    : challengeData.rankChange > 0
    ? strings['leaderboard/personal-ranking/caret-tooltip/up']
    : strings['leaderboard/personal-ranking/caret-tooltip/down'];

  return (
    <RankingWrapper>
      <Spacing sizeTop='kilo' sizeBottom='kilo' sizeLeft='byte' sizeRight='byte' width='17.5%'>
        <CircleText as='span' color='#008A15' size='byte' width='45px' bold>
          {rank != null ? Number(rank).toLocaleString(`${language || 'en'}-CA`) : '--'}
        </CircleText>
      </Spacing>
      <StatRow>
        <StatTopRow>
          <Cell textAlign='left'>
            <Text as='span' noMargin size='tera'>
              {username}
            </Text>
          </Cell>
          <Cell>
            <Text as='span' noMargin size='tera'>
              {correctPicks}
            </Text>
          </Cell>
          <Cell>
            <Text as='span' noMargin size='tera'>
              {(pickAccuracy || '--') + '%'}
            </Text>
          </Cell>
          <Cell>
            <HeatStreakText variant={scoringStreak > 0 ? 'hot' : 'cold'} showIcon size='giga' text={scoringStreak} />
          </Cell>
        </StatTopRow>
        {showRankingChange && (
          <RankingChangeRow>
            <ColorProvider variant='grey'>
              <Text as='span' size='mega' style={{ paddingLeft: '0.3em' }}>
                {rankChangeText}
              </Text>
            </ColorProvider>
            <Spacing sizeLeft='bit' width='auto'>
              <ColorProvider variant={rankChange === 0 ? 'grey' : rankChange > 0 ? 'g800' : 'r300'}>
                <Text as='span' size='mega'>
                  {Math.abs(rankChange)}
                </Text>
              </ColorProvider>
            </Spacing>
            <IconContainer>
              {rankChange !== 0 && (
                <>
                  <ColorProvider variant={rankChange > 0 ? 'g800' : 'r300'}>
                    <Icon icon={rankChange > 0 ? 'caret-up' : 'caret-down'} size='sm' aria-hidden />
                  </ColorProvider>
                  <ScreenReaderText size='mega'>{caretTooltip}</ScreenReaderText>
                </>
              )}
            </IconContainer>
          </RankingChangeRow>
        )}
      </StatRow>
    </RankingWrapper>
  );
};

PersonalRanking.propTypes = {
  showRankingChange: PropTypes.bool,
  rankChangeText: PropTypes.string,
  caretTooltip: PropTypes.string,
  rankChange: PropTypes.number,
  correctPicks: PropTypes.number,
  pickAccuracy: PropTypes.number,
  rank: PropTypes.number,
  scoringStreak: PropTypes.number,
  username: PropTypes.string,
  language: PropTypes.string,
};

export default PersonalRanking;
